import { useEffect, useMemo, useState } from 'react'
import { useUser } from 'headlo-auth'
import { OrdersScreen } from './screens/OrdersScreen'
import { EarningsScreen } from './screens/EarningsScreen'
import { SettingsScreen } from './screens/SettingsScreen'
import { useShopContext } from './ShopContext'
import { ShopPicker } from './ShopPicker'
import { usePushNotifications } from './usePushNotifications'
import { useWhoAmI } from './WhoAmIContext'

// Top-level app shell — bottom-tab navigation between Orders, Earnings,
// and Settings. v0.1.0 covers three tabs; Messages deferred to Wave 4.5
// (whole new subsystem, not built yet — see wave plan).
//
// Tab state is a simple useState — no router. Mobile flow is shallow and
// the tabs are peers, not nested routes. If we later add drill-down URLs
// (order/:id) we'll introduce a router at that point.

type Tab = 'orders' | 'earnings' | 'settings'

export default function App() {
  const [tab, setTab] = useState<Tab>('orders')
  const { state: shopState } = useShopContext()
  const { refresh: refreshWhoAmI } = useWhoAmI()

  // When the merchant has no shops, land them on Settings — the only tab
  // with useful content in that state. Fires ONCE when the shop state
  // resolves to 'no-shops'; if they later create a shop and the state
  // flips to 'ready', we don't reset — respect wherever they navigated to.
  const [autoJumpedNoShops, setAutoJumpedNoShops] = useState(false)
  useEffect(() => {
    if (shopState.status === 'no-shops' && !autoJumpedNoShops) {
      setTab('settings')
      setAutoJumpedNoShops(true)
    }
  }, [shopState.status, autoJumpedNoShops])

  // Push notifications — register the device on sign-in, refresh whoami on
  // any foreground push, jump to Orders tab on notification tap. Handlers
  // are memoized so the effect inside usePushNotifications doesn't re-run
  // on every render.
  //
  // Tap deep-linking is minimal for v0.1: route to Orders tab. Full "open
  // this specific order's detail" is a Wave 4.5 add — needs a router or an
  // App-level "pendingOrderId" state to consume. When pushes actually
  // start arriving on real devices we'll know if the shallower version
  // ships enough value.
  const pushHandlers = useMemo(() => ({
    onForeground: () => { refreshWhoAmI() },
    onTap: (payload: { notification: { data?: Record<string, string> } }) => {
      const route = payload.notification?.data?.['route']
      if (route === 'order-detail') setTab('orders')
      refreshWhoAmI()
    },
  }), [refreshWhoAmI])
  usePushNotifications(pushHandlers)

  // Boot-time shop selection gates access to the bottom-tab shell. Every
  // screen below assumes a shop is selected, so we hold the tabs out of
  // the tree until we have one. Same pattern as SignInGate holding the
  // whole app until sign-in.
  if (shopState.status === 'loading') {
    return (
      <div style={s.centered}>
        <div style={s.spinnerDot} />
        <div style={s.spinnerText}>Loading shops…</div>
      </div>
    )
  }

  if (shopState.status === 'error') {
    return (
      <div style={s.centered}>
        <div style={s.errCard}>
          <div style={s.errTitle}>Couldn't load your shops</div>
          <div style={s.errMsg}>{shopState.message}</div>
        </div>
      </div>
    )
  }

  if (shopState.status === 'needs-picker') {
    return <ShopPicker />
  }

  // shopState.status === 'ready' OR 'no-shops' — render the shell.
  // 'no-shops' still needs the tab bar so the user can reach Settings
  // (sign out, account info). Orders + Earnings screens handle the
  // no-shop case with their own empty states.
  return (
    <div style={s.app}>
      <main style={s.main}>
        {tab === 'orders'   && <OrdersScreen />}
        {tab === 'earnings' && <EarningsScreen />}
        {tab === 'settings' && <SettingsScreen />}
      </main>

      <nav style={s.tabBar}>
        <TabButton label="Orders"   icon="📦" active={tab === 'orders'}   onClick={() => setTab('orders')} />
        <TabButton label="Earnings" icon="💰" active={tab === 'earnings'} onClick={() => setTab('earnings')} />
        <TabButton label="Settings" icon="⚙️" active={tab === 'settings'} onClick={() => setTab('settings')} />
      </nav>
    </div>
  )
}

function TabButton({ label, icon, active, onClick }: { label: string; icon: string; active: boolean; onClick: () => void }) {
  return (
    <button
      style={{ ...s.tabBtn, ...(active ? s.tabBtnActive : {}) }}
      onClick={onClick}
    >
      <span style={s.tabIcon}>{icon}</span>
      <span style={s.tabLabel}>{label}</span>
    </button>
  )
}

// Silence lint on the useUser import — expected use is in Settings, but
// keeping the import here makes future top-bar/greeting additions trivial.
void useUser

const s = {
  app: {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100vh',
    background: '#faf9f6',
    color: '#1a1a18',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  main: {
    flex: 1,
    overflowY: 'auto' as const,
    // Safe area for iOS notch on the top edge; tab bar handles bottom.
    paddingTop: 'env(safe-area-inset-top)',
  },
  tabBar: {
    display: 'flex',
    borderTop: '1px solid #e5e5e0',
    background: '#ffffff',
    paddingBottom: 'env(safe-area-inset-bottom)',
    // iOS-style backdrop-blur if the OS supports it, degrades to solid.
    backdropFilter: 'saturate(180%) blur(20px)',
    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
  },
  tabBtn: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 2,
    padding: '8px 4px 6px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#8a8a80',
    fontFamily: 'inherit',
  },
  tabBtnActive: {
    color: '#1a1a18',
  },
  tabIcon: {
    fontSize: 22,
    lineHeight: 1,
  },
  tabLabel: {
    fontSize: 10,
    fontWeight: 600,
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#faf9f6',
    color: '#1a1a18',
    padding: 24,
    boxSizing: 'border-box' as const,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  spinnerDot: {
    width: 28,
    height: 28,
    border: '3px solid #e5e5e0',
    borderTopColor: '#1a1a18',
    borderRadius: '50%',
    margin: '0 auto 14px',
    animation: 'headloSpin 0.7s linear infinite',
  },
  spinnerText: {
    fontSize: 13,
    color: '#8a8a80',
    textAlign: 'center' as const,
  },
  errCard: {
    maxWidth: 360,
    textAlign: 'center' as const,
  },
  errTitle: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 8,
  },
  errMsg: {
    fontSize: 14,
    color: '#5a5a55',
    lineHeight: 1.55,
  },
} as const
